import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaTimes, FaEdit, FaTrash, FaSave, FaEllipsisV, FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useBaseComponentFullscreen } from './BaseComponent';

export default function TableComponent({ component, onUpdate }) {
  const data = component.data || { tabs: [], active_tab: 0 };
  const activeTabData = data.tabs[data.active_tab] || { columns: [], rows: 0 };
  const [editingTabIndex, setEditingTabIndex] = useState(null);
  const [editTabName, setEditTabName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editingColumn, setEditingColumn] = useState(null);
  const [editColumnName, setEditColumnName] = useState('');
  const [cellContent, setCellContent] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const tableRef = useRef(null);
  const debounceTimer = useRef(null);

  // Calculate if table should be full width
  const [isFullWidth, setIsFullWidth] = useState(false);
  useEffect(() => {
    if (tableRef.current) {
      const tableWidth = tableRef.current.scrollWidth;
      const containerWidth = tableRef.current.parentElement.clientWidth;
      setIsFullWidth(tableWidth <= containerWidth);
    }
  }, [activeTabData.columns, activeTabData.rows]);

  // Add new tab
  const addTab = () => {
    const defaultColumns = [
      { name: 'Column 1', cells: Array(4).fill({ content: '' }) },
      { name: 'Column 2', cells: Array(4).fill({ content: '' }) },
      { name: 'Column 3', cells: Array(4).fill({ content: '' }) }
    ];
    
    const newTabs = [
      ...data.tabs,
      {
        name: `Table ${data.tabs.length + 1}`,
        columns: defaultColumns,
        rows: 4,
        created_at: new Date().toISOString()
      }
    ];
    
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: newTabs.length - 1
      }
    });
  };

  // Add column at position
  const addColumn = (position) => {
    const updatedTabs = [...data.tabs];
    const newCol = {
      name: `Column ${updatedTabs[data.active_tab].columns.length + 1}`,
      cells: Array(Math.max(activeTabData.rows, 1)).fill({ content: '' })
    };
    
    updatedTabs[data.active_tab].columns.splice(position, 0, newCol);
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: updatedTabs
      }
    });
  };

  // Add row at position
  const addRow = (position) => {
    const updatedTabs = [...data.tabs];
    updatedTabs[data.active_tab].rows = Math.max(updatedTabs[data.active_tab].rows + 1, 1);
    
    updatedTabs[data.active_tab].columns.forEach(col => {
      col.cells.splice(position, 0, { content: '', created_at: new Date().toISOString() });
      // Ensure we have enough cells if adding to empty table
      while (col.cells.length < updatedTabs[data.active_tab].rows) {
        col.cells.push({ content: '', created_at: new Date().toISOString() });
      }
    });
    
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: updatedTabs
      }
    });
  };
  // Delete column
  const deleteColumn = (colIndex) => {
    if (activeTabData.columns.length <= 1) return;
    
    const updatedTabs = [...data.tabs];
    updatedTabs[data.active_tab].columns.splice(colIndex, 1);
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: updatedTabs
      }
    });
  };

  // Delete row
  const deleteRow = (rowIndex) => {
    if (activeTabData.rows <= 1) return;
    
    const updatedTabs = [...data.tabs];
    updatedTabs[data.active_tab].rows = Math.max(updatedTabs[data.active_tab].rows - 1, 1);
    updatedTabs[data.active_tab].columns.forEach(col => {
      col.cells.splice(rowIndex, 1);
    });
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: updatedTabs
      }
    });
  };

  // Update cell content
  const updateCell = (rowIndex, colIndex, content) => {
    const updatedTabs = [...data.tabs];
    updatedTabs[data.active_tab].columns[colIndex].cells[rowIndex] = {
      content,
      updated_at: new Date().toISOString()
    };
    
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
    }, 1500);
  };

  // Handle context menu
  const [containerEl, setContainerEl] = useState(null);
  const handleContextMenu = (e, type, index) => {
    e.preventDefault();
    // Find the scrollable container (the one with overflow-x-auto)
    let container = e.currentTarget;
    while (container && !container.classList.contains('overflow-x-auto')) {
      container = container.parentElement;
    }
    
    // If we can't find the overflow container, use the table container
    if (!container) {
      container = tableRef.current?.parentElement;
    }
    
    if (container) setContainerEl(container);
    
    // Use viewport coordinates for positioning
    let x = e.clientX;
    let y = e.clientY;

    // Menu size estimate
    const menuWidth = 180;
    const menuHeight = 120;

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Prevent overflow right
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 10;
    }

    // Prevent overflow bottom
    if (y + menuHeight > viewportHeight) {
      y = viewportHeight - menuHeight - 10;
    }

    // Prevent overflow left
    if (x < 10) {
      x = 10;
    }

    // Prevent overflow top
    if (y < 10) {
      y = 10;
    }

    setContextMenu({ type, index, x, y });
  };

  // Close context menu
  const closeContextMenu = () => {
    setContextMenu(null);
  };

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleMouseDown = (e) => {
      // Only close if clicking outside the context menu
      if (!e.target.closest || !e.target.closest('.table-context-menu')) {
        closeContextMenu();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // Start editing a cell
  const startEditingCell = (rowIndex, colIndex) => {
    setEditingCell({ row: rowIndex, col: colIndex });
    setCellContent(activeTabData.columns[colIndex].cells[rowIndex]?.content || '');
  };

  // Save cell edit
  const saveCellEdit = () => {
    if (editingCell) {
      updateCell(editingCell.row, editingCell.col, cellContent);
      setEditingCell(null);
    }
  };

  // Delete tab
  const deleteTab = (index) => {
    const newTabs = data.tabs.filter((_, i) => i !== index);
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: Math.min(data.active_tab, newTabs.length - 1)
      }
    });
    setDeleteConfirm(null);
  };

  // Start editing tab name
  const startEditingTab = (index) => {
    setEditingTabIndex(index);
    setEditTabName(data.tabs[index].name);
  };

  // Save tab name
  const saveTabName = () => {
    if (editTabName.trim()) {
      const updatedTabs = [...data.tabs];
      updatedTabs[editingTabIndex].name = editTabName.trim();
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
      setEditingTabIndex(null);
    }
  };

  // Start editing column name
  const startEditingColumn = (colIndex, e) => {
    e.stopPropagation();
    setEditingColumn(colIndex);
    setEditColumnName(activeTabData.columns[colIndex].name);
  };

  // Save column name
  const saveColumnName = (colIndex) => {
    if (editColumnName.trim()) {
      const updatedTabs = [...data.tabs];
      updatedTabs[data.active_tab].columns[colIndex].name = editColumnName.trim();
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
      setEditingColumn(null);
    }
  };

  const isFullscreen = useBaseComponentFullscreen();

  return (
    <div className="table-component">
      {/* Tab Bar */}
      <div className="tab-bar flex flex-wrap items-center gap-1 mb-4">
        {data.tabs.map((tab, index) => (
          <div
            key={index}
            className={`flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition-colors text-sm ${
              index === data.active_tab 
                ? 'bg-[var(--primary)] text-white'
                : 'bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)]'
            }`}
            onClick={() => onUpdate({
              ...component,
              data: {
                ...data,
                active_tab: index
              }
            })}
          >
            {editingTabIndex === index ? (
              <input
                type="text"
                className="px-1 bg-transparent rounded focus:outline-none w-20"
                value={editTabName}
                onChange={(e) => setEditTabName(e.target.value)}
                onBlur={saveTabName}
                onKeyPress={(e) => e.key === 'Enter' && saveTabName()}
                autoFocus
              />
            ) : (
              <>
                <span className={`truncate ${isFullscreen ? 'max-w-[200px]' : 'max-w-[120px]'}`} title={tab.name}>{tab.name}</span>
                <button 
                  className="p-1 hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditingTab(index);
                  }}
                >
                  <FaEdit size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
                </button>
              </>
            )}
            
            {data.tabs.length > 1 && (
              <button
                className="p-1 hover:opacity-80"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm({ index, tabName: tab.name });
                }}
              >
                <FaTimes size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
              </button>
            )}
          </div>
        ))}
        
        <button
          className="p-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-sm"
          onClick={addTab}
          title="Add new table"
        >
          <FaPlus size={12} />
        </button>
      </div>
      
      {/* Table Content */}
      <div className={`overflow-x-auto ${isFullWidth ? 'flex justify-center' : ''}`}>
        <table 
          ref={tableRef}
          className={`border-collapse ${isFullWidth ? 'w-full' : 'min-w-full'}`}
        >
          <thead>
            <tr>
              {activeTabData.columns.map((col, colIndex) => (
                <th 
                  key={colIndex} 
                  className="border border-[var(--bg-hover)] p-2 bg-[var(--bg-hover)] text-left relative group"
                  onContextMenu={(e) => handleContextMenu(e, 'column', colIndex)}
                >
                  {editingColumn === colIndex ? (
                    <input
                      type="text"
                      className="w-full bg-transparent border-b border-[var(--primary)] focus:outline-none"
                      value={editColumnName}
                      onChange={(e) => setEditColumnName(e.target.value)}
                      onBlur={() => saveColumnName(colIndex)}
                      onKeyPress={(e) => e.key === 'Enter' && saveColumnName(colIndex)}
                      autoFocus
                    />
                  ) : (
                    <div className="flex items-center justify-between">
                      <span onClick={(e) => startEditingColumn(colIndex, e)} className="cursor-text">
                        {col.name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleContextMenu(e, 'column', colIndex);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                      >
                        <FaEllipsisV size={12} />
                      </button>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.max(activeTabData.rows, 1) }).map((_, rowIndex) => (
              <tr 
                key={rowIndex}
                className="group"
                onContextMenu={(e) => handleContextMenu(e, 'row', rowIndex)}
              >
                {activeTabData.columns.map((col, colIndex) => (
                  <td 
                    key={colIndex}
                    className="border border-[var(--bg-hover)] p-2 relative"
                    onClick={() => startEditingCell(rowIndex, colIndex)}
                  >
                    {editingCell?.row === rowIndex && editingCell?.col === colIndex ? (
                      <div className="absolute inset-0 p-2 flex items-center">
                        <input
                          type="text"
                          className="w-full h-full p-1 border border-[var(--primary)] rounded bg-[var(--bg-primary)]"
                          value={cellContent}
                          onChange={(e) => setCellContent(e.target.value)}
                          onBlur={saveCellEdit}
                          onKeyPress={(e) => e.key === 'Enter' && saveCellEdit()}
                          autoFocus
                        />
                      </div>
                    ) : (
                      <div className="min-h-6">
                        {col.cells[rowIndex]?.content || ''}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Context Menu */}
      {contextMenu &&
        createPortal(
          <div
            className="table-context-menu fixed z-50 bg-[var(--bg-primary)] shadow-lg rounded-md border border-[var(--bg-hover)] py-1"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={e => e.stopPropagation()}
          >
            {contextMenu.type === 'column' && (
              <>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-[var(--bg-hover)] flex items-center gap-2"
                  onClick={() => {
                    addColumn(contextMenu.index);
                    closeContextMenu();
                  }}
                >
                  <FaArrowLeft size={12} /> Add Column Before
                </button>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-[var(--bg-hover)] flex items-center gap-2"
                  onClick={() => {
                    addColumn(contextMenu.index + 1);
                    closeContextMenu();
                  }}
                >
                  <FaArrowRight size={12} /> Add Column After
                </button>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-[var(--bg-hover)] text-danger flex items-center gap-2"
                  onClick={() => {
                    deleteColumn(contextMenu.index);
                    closeContextMenu();
                  }}
                >
                  <FaTrash size={12} /> Delete Column
                </button>
              </>
            )}
            {contextMenu.type === 'row' && (
              <>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-[var(--bg-hover)] flex items-center gap-2"
                  onClick={() => {
                    addRow(contextMenu.index);
                    closeContextMenu();
                  }}
                >
                  <FaArrowUp size={12} /> Add Row Above
                </button>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-[var(--bg-hover)] flex items-center gap-2"
                  onClick={() => {
                    addRow(contextMenu.index + 1);
                    closeContextMenu();
                  }}
                >
                  <FaArrowDown size={12} /> Add Row Below
                </button>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-[var(--bg-hover)] text-danger flex items-center gap-2"
                  onClick={() => {
                    deleteRow(contextMenu.index);
                    closeContextMenu();
                  }}
                >
                  <FaTrash size={12} /> Delete Row
                </button>
              </>
            )}
          </div>,
          document.body
        )
      }
      
      {/* Delete confirmation modal */}
      {deleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-[var(--bg-primary)] p-4 rounded-lg max-w-sm w-full mx-4 max-h-[90vh] overflow-y-auto">
            <p className="mb-4">Are you sure you want to delete "{deleteConfirm.tabName}"?</p>
            <div className="flex justify-end gap-2">
              <button 
                className="px-3 py-1 rounded bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)]"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="px-3 py-1 rounded bg-danger hover:bg-danger hover:scale-105 hover:shadow-lg text-white transition-all duration-200"
                onClick={() => deleteTab(deleteConfirm.index)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}